import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Bars3CenterLeftIcon, BarsArrowDownIcon, PlusIcon, TableCellsIcon } from '@heroicons/react/24/outline'
import {
  addSection,
  deleteSection,
  selectData,
  resetForm as resetConstructorForm,
  initForm as initConstructorForm,
  changeRequestStatus as changeConstructorRequestStatus,
  selectRequestStatus as selectConstructorRequestStatus,
} from '../../../../../../reducers/meeting-type-task/constructor'
import OutsideClickHandler from '../../../../../common/outside-click-handler/OutsideClickHandler'
import UpdateCellContent from './table/update-cell-content/UpdateCellContent'
import { useParams } from 'react-router-dom'
import { selectTaskDetails } from '../../../../../../reducers/meeting-type-task/task-details'
import { updateTaskContent } from './slice'
import { selectTypeDetails } from '../../../../../../reducers/meeting-type/type-details'
import UnsavedDataNotification from './unsaved-data-notification/UnsaqvedDataNotification'
import { REQUEST_STATUSES } from '../../../../../../reducers/reducer-helper'
import Loader from '../../../../../common/loader/Loader'
import { QueueListIcon } from '@heroicons/react/24/outline/index'
import ListSection from './list/ListSection'
import TextSection from './text/TextSection'
import TableSection from './table/TableSection'
import DynamicTableSection from './dynamic-table/DynamicTableSection'
import { classNames } from '../../../../../../utils/className'
import AddDynamicTableColumnForm from './dynamic-table/add-column-form/AddColumnForm'
import UpdateDynamicTableColumnForm from './dynamic-table/update/UpdateColumnForm'

export default function TaskConstructor() {
  const dispatch = useDispatch()

  const [showTypes, setShowTypes] = useState(false)
  const [navigateConfirmModal, setNavigateConfirmModal] = useState(false)
  const [renamingSectionUuid, setRenamingSectionUuid] = useState(null)

  const { taskUuid } = useParams()

  const task = useSelector(selectTaskDetails)
  const type = useSelector(selectTypeDetails)
  const constructorRequestStatus = useSelector(selectConstructorRequestStatus)

  const { sections } = useSelector(selectData)

  const addTextSection = () => {
    const structure = {
      type: 'text',
      uuid: (Math.random() + 1).toString(36).substring(2),
      name: '',
      data: { assignment: '' },
    }

    dispatch(addSection({ section: structure }))
    setShowTypes(false)
  }

  const addListSection = () => {
    const structure = {
      type: 'list',
      uuid: (Math.random() + 1).toString(36).substring(2),
      name: '',
      data: { assignment: '' },
    }

    dispatch(addSection({ section: structure }))
    setShowTypes(false)
  }
  const addTableSection = () => {
    const structure = {
      type: 'table',
      uuid: (Math.random() + 1).toString(36).substring(2),
      data: {
        cols: [],
        rows: [{ cells: [] }],
      },
    }

    dispatch(addSection({ section: structure }))
    setShowTypes(false)
  }
  const addDynamicTableSection = () => {
    const structure = {
      type: 'dynamic-table',
      uuid: (Math.random() + 1).toString(36).substring(2),
      data: {
        assignment: '',
        cols: [],
      },
    }

    dispatch(addSection({ section: structure }))
    setShowTypes(false)
  }

  const sectionTypes = [
    {
      id: 'text-section',
      name: 'Text',
      handler: addTextSection,
      icon: Bars3CenterLeftIcon,
    },
    {
      id: 'list-section',
      name: 'List',
      handler: addListSection,
      icon: BarsArrowDownIcon,
    },
    {
      id: 'table-section',
      name: 'Table',
      handler: addTableSection,
      icon: TableCellsIcon,
    },
    {
      id: 'dynamic-table-section',
      name: 'Dynamic table',
      handler: addDynamicTableSection,
      icon: QueueListIcon,
    },
  ]

  const deleteSectionProcess = (index) => {
    dispatch(deleteSection({ indexForDelete: index }))
  }

  useEffect(() => {
    return () => {
      dispatch(resetConstructorForm())
    }
  }, [])

  useEffect(() => {
    if (task) {
      dispatch(resetConstructorForm())
      dispatch(initConstructorForm({ sections: task.content ? task.content : [] }))
    }
  }, [task])

  const saveContent = (event) => {
    if (event) {
      event.preventDefault()
    }

    dispatch(changeConstructorRequestStatus({ status: REQUEST_STATUSES.PENDING }))
    dispatch(updateTaskContent({ taskUuid, typeUuid: type.uuid, content: sections }))
    setRenamingSectionUuid(null)
  }

  const shouldBlockNavigation = () => {
    if (task.content !== sections) {
      setNavigateConfirmModal(true)
      return 'Are you sure you want to leave? You may lose unsaved changes.'
    }
    return null // Allow navigation
  }

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (task.content !== sections) {
        e.preventDefault()
        e.returnValue = '' // required for chrome
        return true // Return a truthy value
      }
      return null // Allow navigation if no conditions met
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [sections])

  return (
    <div className={'my-12 w-full'}>
      {sections.length === 0 && (
        <div className={'w-full text-center text-sm text-gray-500 py-8'}>There are no created sections yet.</div>
      )}
      {sections.length > 0 && (
        <div className={'my-4 grid gap-y-4'}>
          {sections.map((section, index) => (
            <div className={'w-full p-4 bg-gray-50 rounded-xl'} key={`section-${index}`}>
              {section.type === 'text' && (
                <TextSection
                  section={section}
                  saveContent={saveContent}
                  deleteSection={deleteSectionProcess}
                  index={index}
                  renamingSectionUuid={renamingSectionUuid}
                  setRenamingSectionUuid={setRenamingSectionUuid}
                />
              )}

              {section.type === 'list' && (
                <ListSection
                  section={section}
                  saveContent={saveContent}
                  deleteSection={deleteSectionProcess}
                  index={index}
                  renamingSectionUuid={renamingSectionUuid}
                  setRenamingSectionUuid={setRenamingSectionUuid}
                />
              )}

              {section.type === 'table' && (
                <TableSection
                  section={section}
                  saveContent={saveContent}
                  deleteSection={deleteSectionProcess}
                  index={index}
                  renamingSectionUuid={renamingSectionUuid}
                  setRenamingSectionUuid={setRenamingSectionUuid}
                />
              )}

              {section.type === 'dynamic-table' && (
                <DynamicTableSection
                  section={section}
                  saveContent={saveContent}
                  deleteSection={deleteSectionProcess}
                  index={index}
                  renamingSectionUuid={renamingSectionUuid}
                  setRenamingSectionUuid={setRenamingSectionUuid}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <div className={'mb-32'}>
        <button
          type={'button'}
          onClick={() => {
            setShowTypes(true)
          }}
          className={classNames(
            'mx-auto block w-48 h-20 rounded-xl hover:bg-indigo-50 text-center items-center cursor-pointer ring-1 ring-gray-100 transition-all',
            showTypes ? 'bg-indigo-50 opacity-50' : 'bg-gray-50',
          )}
        >
          <div className={'w-full pt-3'}>
            <PlusIcon className={'mx-auto w-6 h-6 text-gray-700'} />
          </div>
          <div className={'text-sm text-gray-500 pt-2'}>Add section</div>
        </button>
        {showTypes && (
          <OutsideClickHandler
            onOutsideClick={() => {
              setShowTypes(false)
            }}
          >
            <div className={'mx-auto mt-2 w-fit grid grid-cols-3 gap-4 bg-gray-50 rounded-xl p-4'}>
              {sectionTypes.map((type) => (
                <button
                  key={type.id}
                  type={'button'}
                  onClick={type.handler}
                  className={
                    'block w-32 p-4 ring-1 ring-gray-200 hover:bg-white cursor-pointer text-center text-sm rounded-md bg-gray-100 transition-all'
                  }
                >
                  <type.icon className={'mx-auto w-6 h-6 mb-2 text-gray-700'} />
                  <span className={'text-gray-500'}>{type.name}</span>
                </button>
              ))}
            </div>
          </OutsideClickHandler>
        )}
      </div>
      <button
        type={'button'}
        onClick={saveContent}
        className={
          'fixed flex items-center bottom-4 right-4 text-sm px-8 shadow-sm py-3 bg-indigo-600 text-white rounded-md font-semibold'
        }
      >
        {constructorRequestStatus === REQUEST_STATUSES.PENDING && <Loader />}
        Save content
      </button>
      <UnsavedDataNotification
        shouldBlockNavigation={shouldBlockNavigation}
        showNavigateConfirmModal={navigateConfirmModal}
        setNavigateConfirmModal={setNavigateConfirmModal}
      />
      <UpdateCellContent />
      <UpdateDynamicTableColumnForm />
      <AddDynamicTableColumnForm />
    </div>
  )
}
